import React, {
  ComponentProps,
  PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import clsx from 'clsx';
import {
  Document,
  Page,
} from 'react-pdf';
import IconButton from 'core/components/IconButton';
import ToolTip from 'core/components/Tooltip';
import {
  AutoFillAliasDTO,
  FormPageView,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import {
  PdfTemplateDocument,
  PdfTemplateField,
  PdfPageRect,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  getAutoFillFieldTooltip,
  hasAutoFillAlias,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldAlias';
import {
  getDraftRectClassNames,
  getFieldAssignee,
  getFieldBoxClassNames,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldAssigneeStyles';
import {
  getCanvasFieldTypeIcon,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldTypeIcons';
import {
  isNilOrZero,
} from '@populateui/utils';
import isNil from 'lodash/isNil';

type Props = {
  document: PdfTemplateDocument;
  pages?: FormPageView[] | null;
  fields: PdfTemplateField[];
  selectedFieldId: string | null;
  selectedFieldIds: string[];
  zoom: number;
  aliasOptions?: AutoFillAliasDTO[] | null;
  onPageCountChange?: (pageCount: number) => void;
  onFieldDraw: (pageNumber: number, rect: PdfPageRect) => void;
  onFieldRemove: (fieldId: string) => void;
  onFieldRectChange: (fieldId: string, rect: PdfPageRect) => void;
  onFieldPositionChange: (
    fieldId: string,
    pageNumber: number,
    rect: PdfPageRect,
  ) => void;
  onFieldTransformStart?: (fieldId: string) => void;
  onFieldTransformEnd?: (fieldId: string) => void;
  onSelectField: (fieldId: string | null, options?: { multi?: boolean }) => void;
  onZoomChange: (zoom: number) => void;
  defaultAssigneeForNewField?: FormFieldAssignee;
};

type ResizeHandle = 'e' | 'n' | 'nw' | 's' | 'se' | 'sw' | 'w';

type PointerState = {
  type: 'draw' | 'move' | 'resize';
  pageNumber: number;
  fieldId?: string;
  fieldIds?: string[];
  handle?: ResizeHandle;
  originalRect?: PdfPageRect;
  originalRects?: Record<string, PdfPageRect>;
  originalBounds?: PdfPageRect;
  originalPageHeight?: number;
  currentPageNumber?: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
};

type PageDefinition = {
  pageNumber: number;
  width: number;
  height: number;
  rotation: number;
};

type PdfDocumentProxy = Parameters<
  NonNullable<ComponentProps<typeof Document>['onLoadSuccess']>
>[0];

const zoomStep = 0.1;
const minimumZoom = 0.8;
const maximumZoom = 1.4;
const minimumFieldSize = 8;
const maximumRenderedPageWidth = 870;
const minimumWidthForVerticalMiddleHandles = 44;
const minimumHeightForHorizontalMiddleHandles = 28;
const minimumCompactFieldWidthForLabel = 72;
const minimumCompactFieldHeightForLabel = 24;
const defaultPageDefinition: PageDefinition = {
  pageNumber: 0,
  width: 612,
  height: 792,
  rotation: 0,
};
const resizeHandleClassNames: Record<ResizeHandle, string> = {
  e: '-right-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize',
  n: 'left-1/2 -top-1.5 -translate-x-1/2 cursor-ns-resize',
  nw: '-left-1.5 -top-1.5 cursor-nwse-resize',
  s: 'bottom-0 left-1/2 translate-y-1/2 -translate-x-1/2 cursor-ns-resize',
  sw: '-bottom-1.5 -left-1.5 cursor-nesw-resize',
  se: '-bottom-1.5 -right-1.5 cursor-nwse-resize',
  w: '-left-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize',
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getRoundedRenderedSize(size: number, renderScale: number) {
  return Math.round(size * renderScale);
}

function getPageDefinitions(
  pages?: FormPageView[] | null,
  pageCount?: number,
): PageDefinition[] {
  const pageDefinitions = pages?.map((pageView, index) => ({
    pageNumber: pageView.pageNumber ?? index,
    width: pageView.width ?? defaultPageDefinition.width,
    height: pageView.height ?? defaultPageDefinition.height,
    rotation: pageView.rotation ?? defaultPageDefinition.rotation,
  })).sort((left, right) => left.pageNumber - right.pageNumber);

  if (pageDefinitions?.length) {
    return pageDefinitions;
  }

  return Array.from(
    { length: Math.max(pageCount ?? 1, 1) },
    (_, index) => ({
      ...defaultPageDefinition,
      pageNumber: index,
    }),
  );
}

async function getPageDefinitionsFromDocument(
  pdfDocument: PdfDocumentProxy,
): Promise<PageDefinition[]> {
  const pageDefinitions = await Promise.all(
    Array.from({ length: pdfDocument.numPages }, async (_, index) => {
      const pageNumber = index + 1;
      const page = await pdfDocument?.getPage(pageNumber);
      const viewport = page?.getViewport({ scale: 1 });

      return {
        pageNumber: pageNumber - 1,
        width: viewport?.width,
        height: viewport?.height,
        rotation: viewport?.rotation,
      };
    }),
  );

  return pageDefinitions;
}

async function loadPageDefinitions(
  pdfDocument: PdfDocumentProxy,
  onSuccess: (pageDefinitions: PageDefinition[]) => void,
  onError: () => void,
) {
  try {
    const nextPageDefinitions = await getPageDefinitionsFromDocument(pdfDocument);
    onSuccess(nextPageDefinitions);
  } catch {
    onError();
  }
}

function getNormalizedRect(drawState: PointerState): PdfPageRect {
  const x = Math.min(drawState.startX, drawState.currentX);
  const y = Math.min(drawState.startY, drawState.currentY);
  const width = Math.abs(drawState.currentX - drawState.startX);
  const height = Math.abs(drawState.currentY - drawState.startY);

  return {
    x,
    y,
    width,
    height,
  };
}

function getCssRectFromPdfRect(rect: PdfPageRect, pageHeight: number): PdfPageRect {
  return {
    ...rect,
    y: pageHeight - rect.y - rect.height,
  };
}

function getPdfRectFromCssRect(rect: PdfPageRect, pageHeight: number): PdfPageRect {
  return {
    ...rect,
    y: pageHeight - rect.y - rect.height,
  };
}

function getGroupCssRect(
  rects: PdfPageRect[],
  pageHeight: number,
): PdfPageRect | null {
  if (rects.length === 0) {
    return null;
  }

  const cssRects = rects.map((rect) => getCssRectFromPdfRect(rect, pageHeight));
  const left = Math.min(...cssRects.map((rect) => rect.x));
  const top = Math.min(...cssRects.map((rect) => rect.y));
  const right = Math.max(...cssRects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...cssRects.map((rect) => rect.y + rect.height));

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
}

function getPointerStateGroupData(
  fields: PdfTemplateField[],
  pageHeight: number,
): Pick<PointerState, 'fieldIds' | 'originalRects' | 'originalBounds'> {
  return {
    fieldIds: fields.map((field) => field.id),
    originalRects: Object.fromEntries(
      fields.map((field) => [field.id, field.rect]),
    ),
    originalBounds: getGroupCssRect(
      fields.map((field) => field.rect),
      pageHeight,
    ) ?? undefined,
  };
}

function getMovedRect(
  pointerState: PointerState,
  pagePoint: { x: number; y: number },
  pageWidth: number,
  pageHeight: number,
): PdfPageRect | null {
  if (isNil(pointerState.originalRect) || isNil(pointerState.originalPageHeight)) {
    return null;
  }
  const originalRect = getCssRectFromPdfRect(
    pointerState.originalRect,
    pointerState.originalPageHeight,
  );
  const pointerOffsetX = pointerState.startX - originalRect.x;
  const pointerOffsetY = pointerState.startY - originalRect.y;

  return getPdfRectFromCssRect({
    ...originalRect,
    x: clamp(
      pagePoint.x - pointerOffsetX,
      0,
      pageWidth - originalRect.width,
    ),
    y: clamp(
      pagePoint.y - pointerOffsetY,
      0,
      pageHeight - originalRect.height,
    ),
  }, pageHeight);
}

function getResizedRect(
  pointerState: PointerState,
  pagePoint: { x: number; y: number },
  pageWidth: number,
  pageHeight: number,
): PdfPageRect | null {
  if (isNil(pointerState.originalRect) || isNil(pointerState.handle)) {
    return null;
  }
  const originalRect = getCssRectFromPdfRect(pointerState.originalRect, pageHeight);
  const {
    x,
    y,
    width,
    height,
  } = originalRect;
  const right = x + width;
  const bottom = y + height;

  if (pointerState.handle === 'se') {
    return getPdfRectFromCssRect({
      x,
      y,
      width: clamp(pagePoint.x - x, minimumFieldSize, pageWidth - x),
      height: clamp(pagePoint.y - y, minimumFieldSize, pageHeight - y),
    }, pageHeight);
  }
  if (pointerState.handle === 'e') {
    return getPdfRectFromCssRect({
      x,
      y,
      width: clamp(pagePoint.x - x, minimumFieldSize, pageWidth - x),
      height,
    }, pageHeight);
  }
  if (pointerState.handle === 'n') {
    const nextY = clamp(pagePoint.y, 0, bottom - minimumFieldSize);
    return getPdfRectFromCssRect({
      x,
      y: nextY,
      width,
      height: bottom - nextY,
    }, pageHeight);
  }
  if (pointerState.handle === 'nw') {
    const nextX = clamp(pagePoint.x, 0, right - minimumFieldSize);
    const nextY = clamp(pagePoint.y, 0, bottom - minimumFieldSize);
    return getPdfRectFromCssRect({
      x: nextX,
      y: nextY,
      width: right - nextX,
      height: bottom - nextY,
    }, pageHeight);
  }
  if (pointerState.handle === 's') {
    return getPdfRectFromCssRect({
      x,
      y,
      width,
      height: clamp(pagePoint.y - y, minimumFieldSize, pageHeight - y),
    }, pageHeight);
  }
  if (pointerState.handle === 'w') {
    const nextX = clamp(pagePoint.x, 0, right - minimumFieldSize);
    return getPdfRectFromCssRect({
      x: nextX,
      y,
      width: right - nextX,
      height,
    }, pageHeight);
  }
  const nextX = clamp(pagePoint.x, 0, right - minimumFieldSize);
  return getPdfRectFromCssRect({
    x: nextX,
    y,
    width: right - nextX,
    height: clamp(pagePoint.y - y, minimumFieldSize, pageHeight - y),
  }, pageHeight);
}

function applyGroupedMove(
  pointerState: PointerState,
  pagePoint: { x: number; y: number },
  pageNumber: number,
  pageWidth: number,
  pageHeight: number,
  onFieldPositionChange: Props['onFieldPositionChange'],
) {
  const {
    fieldIds,
    originalBounds,
    originalRects,
    originalPageHeight,
    startX,
    startY,
  } = pointerState;
  const groupFieldIds = fieldIds ?? [];
  const hasGroupPosition = !isNil(originalBounds)
    && !isNil(originalRects)
    && !isNil(originalPageHeight);

  if (hasGroupPosition && groupFieldIds.length > 1) {
    const pointerOffsetX = startX - originalBounds.x;
    const pointerOffsetY = startY - originalBounds.y;
    const offsetX = clamp(
      pagePoint.x - pointerOffsetX - originalBounds.x,
      -originalBounds.x,
      pageWidth - originalBounds.x - originalBounds.width,
    );
    const offsetY = clamp(
      pagePoint.y - pointerOffsetY - originalBounds.y,
      -originalBounds.y,
      pageHeight - originalBounds.y - originalBounds.height,
    );

    groupFieldIds.forEach((fieldId) => {
      const originalRect = originalRects[fieldId];
      if (isNil(originalRect)) {
        return;
      }

      const originalCssRect = getCssRectFromPdfRect(originalRect, originalPageHeight);
      onFieldPositionChange(fieldId, pageNumber, getPdfRectFromCssRect({
        ...originalCssRect,
        x: originalCssRect.x + offsetX,
        y: originalCssRect.y + offsetY,
      }, pageHeight));
    });
  }
}

function applyGroupedResize(
  pointerState: PointerState,
  pagePoint: { x: number; y: number },
  pageWidth: number,
  pageHeight: number,
  onFieldRectChange: Props['onFieldRectChange'],
) {
  const {
    fieldIds,
    handle,
    originalBounds,
    originalRects,
  } = pointerState;
  const groupFieldIds = fieldIds ?? [];

  if (
    isNil(originalBounds)
    || isNil(originalRects)
    || isNil(handle)
    || groupFieldIds.length <= 1
  ) {
    return;
  }
  const resizedBoundsPdf = getResizedRect({
    ...pointerState,
    originalRect: getPdfRectFromCssRect(originalBounds, pageHeight),
  }, pagePoint, pageWidth, pageHeight);

  if (isNil(resizedBoundsPdf)) {
    return;
  }

  const resizedBounds = getCssRectFromPdfRect(resizedBoundsPdf, pageHeight);
  const widthRatio = resizedBounds.width / originalBounds.width;
  const heightRatio = resizedBounds.height / originalBounds.height;

  groupFieldIds.forEach((fieldId) => {
    const originalRect = originalRects[fieldId];
    if (isNil(originalRect)) {
      return;
    }

    const originalCssRect = getCssRectFromPdfRect(originalRect, pageHeight);
    const relativeX = originalCssRect.x - originalBounds.x;
    const relativeY = originalCssRect.y - originalBounds.y;

    onFieldRectChange(fieldId, getPdfRectFromCssRect({
      x: resizedBounds.x + (relativeX * widthRatio),
      y: resizedBounds.y + (relativeY * heightRatio),
      width: originalCssRect.width * widthRatio,
      height: originalCssRect.height * heightRatio,
    }, pageHeight));
  });
}

export default function PdfDocumentViewer({
  document,
  pages,
  fields,
  selectedFieldId,
  selectedFieldIds,
  zoom,
  aliasOptions,
  onPageCountChange,
  onFieldDraw,
  onFieldRemove,
  onFieldRectChange,
  onFieldPositionChange,
  onFieldTransformStart,
  onFieldTransformEnd,
  onSelectField,
  onZoomChange,
  defaultAssigneeForNewField = FormFieldAssignee.Patient,
}: Props) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const suppressFieldClickRef = useRef(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [pointerState, setPointerState] = useState<PointerState | null>(null);
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [loadedDocument, setLoadedDocument] = useState<PdfDocumentProxy | null>(null);
  const [loadedPageCount, setLoadedPageCount] = useState(pages?.length ?? 1);
  const [localPageDefinitions, setLocalPageDefinitions] = useState<PageDefinition[] | null>(null);
  const pageDefinitions = useMemo(() => {
    if (!isNilOrZero(pages?.length)) {
      return getPageDefinitions(pages, loadedPageCount);
    }

    if (!isNilOrZero(localPageDefinitions?.length)) {
      return localPageDefinitions;
    }

    return getPageDefinitions(undefined, loadedPageCount);
  }, [loadedPageCount, localPageDefinitions, pages]);

  const widestPageWidth = useMemo(() => Math.max(
    ...(pageDefinitions ?? []).map((pageDefinition) => pageDefinition.width),
  ), [pageDefinitions]);
  const availablePageWidth = viewportWidth > 64 ? viewportWidth - 64 : widestPageWidth;
  const renderedPageWidth = Math.min(availablePageWidth, maximumRenderedPageWidth);
  const fitScale = renderedPageWidth > 0 ? renderedPageWidth / widestPageWidth : 1;
  const renderScale = fitScale * zoom;
  const draftRect = pointerState?.type === 'draw' ? getNormalizedRect(pointerState) : null;

  useEffect(() => {
    setIsDocumentReady(false);
    setPointerState(null);
    setLoadedDocument(null);
    setLoadedPageCount(pages?.length ?? 1);
    setLocalPageDefinitions(null);
  }, [document.url]);

  useEffect(() => {
    if (!isNilOrZero(pages?.length)) {
      setLoadedPageCount(pages?.length ?? 0);
      onPageCountChange?.(pages?.length ?? 0);
    }
  }, [onPageCountChange, pages]);

  useEffect(() => {
    if (isNil(loadedDocument)) {
      return undefined;
    }

    let cancelled = false;

    if (!isNilOrZero(pages?.length)) {
      setIsDocumentReady(true);
      return undefined;
    }

    loadPageDefinitions(
      loadedDocument,
      (nextPageDefinitions) => {
        if (!cancelled) {
          setLocalPageDefinitions(nextPageDefinitions);
          setIsDocumentReady(true);
        }
      },
      () => {
        if (!cancelled) {
          setLocalPageDefinitions(null);
          setIsDocumentReady(true);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [loadedDocument, pages]);

  useEffect(() => {
    const node = viewportRef.current;
    let resizeObserver: ResizeObserver | null = null;

    if (node) {
      const updateViewportWidth = () => {
        setViewportWidth(node.clientWidth);
      };
      updateViewportWidth();
      resizeObserver = new ResizeObserver(updateViewportWidth);
      resizeObserver.observe(node);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, []);

  const getPdfPoint = (
    event: PointerEvent<HTMLElement>,
    pageDefinition: PageDefinition,
  ) => {
    const pageRect = pageRefs.current[pageDefinition.pageNumber]?.getBoundingClientRect();
    const x = pageRect ? (event.clientX - pageRect.left) / renderScale : 0;
    const y = pageRect ? (event.clientY - pageRect.top) / renderScale : 0;

    return {
      x: clamp(x, 0, pageDefinition.width),
      y: clamp(y, 0, pageDefinition.height),
    };
  };

  const getPageDefinitionAtPointer = (event: PointerEvent<HTMLElement>) => {
    const definitions = pageDefinitions ?? [];
    let matchedPage: PageDefinition | null = null;
    let closestPage: PageDefinition | null = definitions[0] ?? null;
    let minDistance = Infinity;

    definitions.forEach((pageDefinition) => {
      const pageRect = pageRefs.current[pageDefinition.pageNumber]?.getBoundingClientRect();

      if (pageRect) {
        const isWithinX = event.clientX >= pageRect.left && event.clientX <= pageRect.right;
        const isWithinY = event.clientY >= pageRect.top && event.clientY <= pageRect.bottom;

        if (isWithinX && isWithinY) {
          matchedPage = pageDefinition;
        }

        const verticalDistance = event.clientY < pageRect.top
          ? pageRect.top - event.clientY
          : Math.max(0, event.clientY - pageRect.bottom);

        if (verticalDistance < minDistance) {
          minDistance = verticalDistance;
          closestPage = pageDefinition;
        }
      }
    });

    return matchedPage ?? closestPage;
  };

  const handlePointerDown = (
    event: PointerEvent<HTMLDivElement>,
    pageDefinition: PageDefinition,
  ) => {
    if (isDocumentReady && event.button === 0) {
      const point = getPdfPoint(event, pageDefinition);
      event.currentTarget.setPointerCapture(event.pointerId);
      setPointerState({
        type: 'draw',
        pageNumber: pageDefinition.pageNumber,
        startX: point.x,
        startY: point.y,
        currentX: point.x,
        currentY: point.y,
      });
    }
  };

  const handlePointerMove = (
    event: PointerEvent<HTMLDivElement>,
    pageDefinition: PageDefinition,
  ) => {
    if (pointerState) {
      const movePageDefinition = pointerState.type === 'move'
        ? getPageDefinitionAtPointer(event)
        : null;
      const activePageDefinition = movePageDefinition ?? pageDefinition;
      const point = getPdfPoint(event, activePageDefinition);
      if (pointerState.type === 'move' && pointerState.fieldId) {
        const movedRect = getMovedRect(
          pointerState,
          point,
          activePageDefinition.width,
          activePageDefinition.height,
        );
        if (movedRect) {
          onFieldPositionChange(
            pointerState.fieldId,
            activePageDefinition.pageNumber,
            movedRect,
          );
        }
      }
      if (pointerState.type === 'move') {
        applyGroupedMove(
          pointerState,
          point,
          activePageDefinition.pageNumber,
          activePageDefinition.width,
          activePageDefinition.height,
          onFieldPositionChange,
        );
      }
      if (pointerState.type === 'resize' && pointerState.fieldId) {
        const resizedRect = getResizedRect(
          pointerState,
          point,
          pageDefinition.width,
          pageDefinition.height,
        );
        if (resizedRect) {
          onFieldRectChange(pointerState.fieldId, resizedRect);
        }
      }
      if (pointerState.type === 'resize') {
        applyGroupedResize(
          pointerState,
          point,
          pageDefinition.width,
          pageDefinition.height,
          onFieldRectChange,
        );
      }
      setPointerState({
        ...pointerState,
        currentPageNumber: activePageDefinition.pageNumber,
        currentX: point.x,
        currentY: point.y,
      });
    }
  };

  const handlePointerUp = (
    event: PointerEvent<HTMLDivElement>,
    pageDefinition: PageDefinition,
  ) => {
    if (pointerState) {
      suppressFieldClickRef.current = pointerState.type !== 'draw';
      const movePageDefinition = pointerState.type === 'move'
        ? getPageDefinitionAtPointer(event)
        : null;
      const activePageDefinition = movePageDefinition ?? pageDefinition;
      const point = getPdfPoint(event, activePageDefinition);
      if (pointerState.type === 'move' && pointerState.fieldId) {
        const movedRect = getMovedRect(
          pointerState,
          point,
          activePageDefinition.width,
          activePageDefinition.height,
        );

        if (movedRect) {
          onFieldPositionChange(
            pointerState.fieldId,
            activePageDefinition.pageNumber,
            movedRect,
          );
        }
      }

      if (pointerState.type === 'move') {
        applyGroupedMove(
          pointerState,
          point,
          activePageDefinition.pageNumber,
          activePageDefinition.width,
          activePageDefinition.height,
          onFieldPositionChange,
        );
      }

      if (pointerState.type === 'resize' && pointerState.fieldId) {
        const resizedRect = getResizedRect(
          pointerState,
          point,
          pageDefinition.width,
          pageDefinition.height,
        );

        if (resizedRect) {
          onFieldRectChange(pointerState.fieldId, resizedRect);
        }
      }

      const rect = getNormalizedRect({
        ...pointerState,
        currentX: point.x,
        currentY: point.y,
      });
      const canCreateField = rect.width >= minimumFieldSize
        && rect.height >= minimumFieldSize;

      if (pointerState.type === 'draw' && canCreateField) {
        onFieldDraw(
          pointerState.pageNumber,
          getPdfRectFromCssRect(rect, activePageDefinition.height),
        );
      } else if (pointerState.type === 'draw') {
        onSelectField(null);
      } else if (pointerState.fieldId) {
        onFieldTransformEnd?.(pointerState.fieldId);
      }
      setPointerState(null);
    }
  };

  const startFieldMove = (
    event: PointerEvent<HTMLButtonElement>,
    field: PdfTemplateField,
    pageDefinition: PageDefinition,
  ) => {
    if (event.shiftKey) {
      event.stopPropagation();
      return;
    }

    if (isDocumentReady && event.button === 0) {
      const point = getPdfPoint(event, pageDefinition);
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      const selectedPageFields = fields.filter(
        (currentField) => currentField.pageNumber === field.pageNumber
          && selectedFieldIds.includes(currentField.id),
      );

      if (selectedPageFields.length > 1 && selectedFieldIds.includes(field.id)) {
        setPointerState({
          type: 'move',
          pageNumber: field.pageNumber,
          ...getPointerStateGroupData(selectedPageFields, pageDefinition.height),
          originalPageHeight: pageDefinition.height,
          currentPageNumber: field.pageNumber,
          startX: point.x,
          startY: point.y,
          currentX: point.x,
          currentY: point.y,
        });
      } else {
        onSelectField(field.id);
        onFieldTransformStart?.(field.id);
        setPointerState({
          type: 'move',
          pageNumber: field.pageNumber,
          fieldId: field.id,
          originalRect: field.rect,
          originalPageHeight: pageDefinition.height,
          currentPageNumber: field.pageNumber,
          startX: point.x,
          startY: point.y,
          currentX: point.x,
          currentY: point.y,
        });
      }
    }
  };

  const startFieldResize = (
    event: PointerEvent<HTMLButtonElement>,
    field: PdfTemplateField,
    handle: ResizeHandle,
    pageDefinition: PageDefinition,
  ) => {
    if (isDocumentReady && event.button === 0) {
      const point = getPdfPoint(event, pageDefinition);
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      const selectedPageFields = fields.filter(
        (currentField) => currentField.pageNumber === field.pageNumber
          && selectedFieldIds.includes(currentField.id),
      );

      if (selectedPageFields.length > 1 && selectedFieldIds.includes(field.id)) {
        setPointerState({
          type: 'resize',
          pageNumber: field.pageNumber,
          fieldIds: selectedPageFields.map((selectedField) => selectedField.id),
          handle,
          ...getPointerStateGroupData(selectedPageFields, pageDefinition.height),
          startX: point.x,
          startY: point.y,
          currentX: point.x,
          currentY: point.y,
        });
      } else {
        onSelectField(field.id);
        onFieldTransformStart?.(field.id);
        setPointerState({
          type: 'resize',
          pageNumber: field.pageNumber,
          fieldId: field.id,
          handle,
          originalRect: field.rect,
          startX: point.x,
          startY: point.y,
          currentX: point.x,
          currentY: point.y,
        });
      }
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      <div
        className={clsx(
          'flex h-full flex-1 flex-col overflow-auto p-8 pb-24',
        )}
        ref={viewportRef}
        onPointerMove={(event) => {
          if (pointerState?.type === 'move') {
            const activePageDefinition = getPageDefinitionAtPointer(event);
            if (activePageDefinition) {
              handlePointerMove(event, activePageDefinition);
            }
          }
        }}
        onPointerUp={(event) => {
          if (pointerState?.type === 'move') {
            const activePageDefinition = getPageDefinitionAtPointer(event);
            if (activePageDefinition) {
              handlePointerUp(event, activePageDefinition);
            }
          }
        }}
      >
        <div className="flex flex-col items-center gap-8">
          <Document
            key={document.url}
            file={document.url}
            loading={(
              <div className="text-sm text-gray-500">Loading Document...</div>
            )}
            error={(
              <div className="text-sm text-gray-500">PDF preview unavailable</div>
            )}
            onLoadSuccess={(pdfDocument) => {
              setLoadedDocument(pdfDocument);
              setLoadedPageCount(pdfDocument.numPages);
              onPageCountChange?.(pdfDocument.numPages);
            }}
            onLoadError={() => {
              setIsDocumentReady(false);
              setLoadedDocument(null);
              setLocalPageDefinitions(null);
            }}
          >
            {pageDefinitions?.map((pageDefinition) => {
              const pageFields = fields.filter(
                (field) => field.pageNumber === pageDefinition.pageNumber,
              );
              const pageDraftRect = pointerState?.pageNumber === pageDefinition.pageNumber
                ? draftRect
                : null;

              return (
                <div key={pageDefinition.pageNumber} className="mb-2 flex flex-col gap-2">
                  <div
                    ref={(node) => {
                      pageRefs.current[pageDefinition.pageNumber] = node;
                    }}
                    className={clsx(
                      'relative origin-top overflow-hidden',
                      'bg-white shadow-sm',
                      {
                        'cursor-crosshair': isDocumentReady,
                        'cursor-wait': !isDocumentReady,
                      },
                    )}
                    style={{
                      width: pageDefinition.width * renderScale,
                      height: pageDefinition.height * renderScale,
                    }}
                    onPointerDown={(event) => {
                      handlePointerDown(event, pageDefinition);
                    }}
                    onPointerMove={(event) => {
                      event.stopPropagation();
                      handlePointerMove(event, pageDefinition);
                    }}
                    onPointerUp={(event) => {
                      event.stopPropagation();
                      handlePointerUp(event, pageDefinition);
                    }}
                  >
                    {isDocumentReady ? (
                      <Page
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        pageNumber={pageDefinition.pageNumber + 1}
                        height={pageDefinition.height * renderScale}
                        width={pageDefinition.width * renderScale}
                        rotate={pageDefinition.rotation}
                        className="pointer-events-none"
                      />
                    ) : null}
                    {pageFields.map((field) => {
                      const cssRect = getCssRectFromPdfRect(
                        field.rect,
                        pageDefinition.height,
                      );
                      const FieldIcon = getCanvasFieldTypeIcon(field.type);
                      const isSelectedField = selectedFieldIds.includes(field.id);
                      const isPrimarySelectedField = field.id === selectedFieldId;
                      const renderedFieldWidth = getRoundedRenderedSize(
                        cssRect.width,
                        renderScale,
                      );
                      const renderedFieldHeight = getRoundedRenderedSize(
                        cssRect.height,
                        renderScale,
                      );
                      const isCompactChoiceField = (
                        (field.type === 'checkbox' || field.type === 'radioGroup' || field.type === 'checkboxGroup')
                        && (
                          renderedFieldWidth < minimumCompactFieldWidthForLabel
                          || renderedFieldHeight < minimumCompactFieldHeightForLabel
                        )
                      );
                      const visibleResizeHandles = (
                        Object.keys(resizeHandleClassNames) as ResizeHandle[]
                      ).filter((handle) => {
                        if ((handle === 'n' || handle === 's')
                          && renderedFieldWidth < minimumWidthForVerticalMiddleHandles) {
                          return false;
                        }

                        if ((handle === 'e' || handle === 'w')
                          && renderedFieldHeight < minimumHeightForHorizontalMiddleHandles) {
                          return false;
                        }

                        return true;
                      });

                      const isAutoFillField = hasAutoFillAlias(field, aliasOptions);

                      return (
                        <div
                          key={field.id}
                          className={clsx('absolute', {
                            'z-10': isSelectedField,
                            'z-20': isPrimarySelectedField,
                          })}
                          style={{
                            left: cssRect.x * renderScale,
                            top: cssRect.y * renderScale,
                            width: cssRect.width * renderScale,
                            height: cssRect.height * renderScale,
                          }}
                        >
                          <ToolTip
                            hide={!isAutoFillField || !field.aliasId}
                            title={getAutoFillFieldTooltip(field.aliasId, aliasOptions)}
                          >
                            <button
                              type="button"
                              className={clsx(
                                getFieldBoxClassNames(
                                  getFieldAssignee(field),
                                  isSelectedField,
                                  isAutoFillField,
                                ),
                                'text-left',
                                {
                                  'gap-1 px-1': isCompactChoiceField,
                                  'gap-2 px-1': !isCompactChoiceField,
                                },
                              )}
                              onPointerDown={(event) => {
                                startFieldMove(event, field, pageDefinition);
                              }}
                              onClick={(event) => {
                                event.stopPropagation();

                                if (suppressFieldClickRef.current) {
                                  suppressFieldClickRef.current = false;
                                  return;
                                }

                                onSelectField(field.id, { multi: event.shiftKey });
                              }}
                            >
                              <FieldIcon className="!h-4 !w-4 shrink-0 text-current" />
                              <span
                                className={clsx(
                                  'min-w-0 truncate text-xs font-semibold text-current',
                                  {
                                    'leading-none': isCompactChoiceField,
                                  },
                                )}
                              >
                                {field.name}
                              </span>
                            </button>
                          </ToolTip>
                          {isSelectedField && (
                            <>
                              <button
                                type="button"
                                aria-label={`Remove ${field.name}`}
                                className={clsx(
                                  'absolute -right-2 -top-2 z-10 flex h-4 w-4',
                                  'items-center justify-center',
                                  'rounded-full bg-black text-white shadow-sm',
                                  'hover:bg-gray-900',
                                )}
                                onPointerDown={(event) => { event.stopPropagation(); }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onFieldRemove(field.id);
                                }}
                              >
                                <CloseIcon className="!h-4 !w-4" />
                              </button>
                              {visibleResizeHandles.map((
                                handle,
                              ) => (
                                <button
                                  key={handle}
                                  type="button"
                                  aria-label={`Resize ${field.name}`}
                                  className={clsx(
                                    'absolute z-10 h-2 w-2 rounded-sm bg-black',
                                    resizeHandleClassNames[handle],
                                  )}
                                  onPointerDown={(event) => {
                                    startFieldResize(event, field, handle, pageDefinition);
                                  }}
                                />
                              ))}
                            </>
                          )}
                        </div>
                      );
                    })}
                    {pageDraftRect && (
                      <div
                        className={getDraftRectClassNames(defaultAssigneeForNewField)}
                        style={{
                          left: pageDraftRect.x * renderScale,
                          top: pageDraftRect.y * renderScale,
                          width: pageDraftRect.width * renderScale,
                          height: pageDraftRect.height * renderScale,
                        }}
                      />
                    )}
                  </div>
                  <div className="flex justify-end px-2 py-1 text-xs text-gray-600">
                    {pageDefinition.pageNumber + 1}
                    {' '}
                    of
                    {' '}
                    {pageDefinitions.length}
                  </div>
                </div>
              );
            })}
          </Document>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 flex justify-center px-6">
        <div
          className={clsx(
            'pointer-events-auto inline-flex items-center gap-3 rounded-full',
            'border border-gray-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur',
          )}
        >
          <IconButton
            tooltip="Zoom out"
            className="!h-9 !w-9 !border !border-gray-200 !bg-white !text-gray-700 hover:!bg-gray-50"
            onClick={() => { onZoomChange(Math.max(minimumZoom, zoom - zoomStep)); }}
          >
            <RemoveRoundedIcon className="!h-5 !w-5" />
          </IconButton>
          <span className="w-14 text-center text-sm font-semibold text-gray-600">
            {Math.round(zoom * 100)}
            %
          </span>
          <IconButton
            tooltip="Zoom in"
            className="!h-9 !w-9 !border !border-gray-200 !bg-white !text-gray-700 hover:!bg-gray-50"
            onClick={() => { onZoomChange(Math.min(maximumZoom, zoom + zoomStep)); }}
          >
            <AddRoundedIcon className="!h-5 !w-5" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
